import React, { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../firebase';
import AppLinksFooter from './AppLinksFooter';
import { isMobilePlatform } from '../utils/platform';

const DEFAULT_CONTENT = `PRIVACY POLICY

Effective Date: June 3, 2026
Last Updated: June 3, 2026

GuffPuff LLC is committed to protecting your privacy. This Privacy Policy explains what personal information we collect, how we use it, and your rights regarding your data when you use the GuffPuff mobile application.

1. INFORMATION WE COLLECT

Account Information: Name, email address, date of birth, and password when you register.
Profile Information: Profile photo, username, bio, and interests.
Usage Data: Features you use, conversations you join, and interaction patterns.
Device Information: Device type, operating system, unique identifiers, and network information.
Log Data: IP address, crash reports, timestamps, and diagnostic data.

2. HOW WE USE YOUR INFORMATION

We use your information to: create and manage your account; provide and improve GuffPuff services; personalize your experience; process subscription payments; send service notifications; monitor for fraud and policy violations; and comply with legal obligations.

3. HOW WE SHARE YOUR INFORMATION

We do not sell your personal data. We may share information with trusted service providers who assist us in operating the platform, or when required by law. Your public profile and content are visible to other GuffPuff users.

4. DATA RETENTION

We retain your data for as long as your account is active. Upon account deletion, your personal data is deleted within 30 days, except where retention is required by law.

5. YOUR RIGHTS

You have the right to access, correct, delete, or export your personal data. To exercise these rights, contact us at privacy@guffpuff.com.

6. SECURITY

We use industry-standard security measures including encryption in transit and encrypted storage to protect your data.

7. CHILDREN\'S PRIVACY

GuffPuff is not intended for children under 13. We do not knowingly collect data from children.

8. CONTACT US

Privacy Team: privacy@guffpuff.com
Support: support@guffpuff.com
Website: www.guffpuff.com`;

const PrivacyPolicyPage = () => {
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('Privacy Policy');
  const [lastUpdated, setLastUpdated] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const hideBack = isMobilePlatform(location.search);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const docRef = doc(db, 'legal_content', 'privacy_policy');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setTitle(data.title || 'Privacy Policy');
          setContent(data.content || DEFAULT_CONTENT);
          if (data.lastUpdated) {
            setLastUpdated(
              new Date(data.lastUpdated.toDate ? data.lastUpdated.toDate() : data.lastUpdated).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })
            );
          }
        } else {
          setContent(DEFAULT_CONTENT);
        }
      } catch (err) {
        console.error('Error fetching Privacy Policy:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div style={{ background: 'linear-gradient(to right, #22d3ee, #0891b2)', position: 'relative' }} className="py-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          {!hideBack && (
          <button
            onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/admin/login')}
            style={{ position: 'absolute', top: '24px', left: '24px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: 'rgba(255,255,255,0.9)', fontSize: '14px', fontWeight: 500, padding: 0 }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          )}
          <img
            src={`${process.env.PUBLIC_URL}/images/logo.png`}
            alt="GUFFPUFF"
            className="h-16 mx-auto mb-4"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <h1 className="text-3xl font-bold text-white">{title}</h1>
          {lastUpdated && (
            <p className="text-cyan-100 mt-2 text-sm">Last updated: {lastUpdated}</p>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto py-10 px-6">
        <div className="bg-white rounded-2xl shadow-sm p-8">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="w-10 h-10 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : content ? (
            <div
              className="prose prose-gray max-w-none text-gray-700 leading-relaxed"
              style={{ whiteSpace: 'pre-wrap', lineHeight: '1.8', fontSize: '15px' }}
            >
              {content}
            </div>
          ) : (
            <p className="text-gray-400 text-center py-20">No content available yet.</p>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="text-center py-6 text-sm text-gray-400 space-y-3">
        <AppLinksFooter variant="muted" />
        <p>&copy; {new Date().getFullYear()} GuffPuff. All rights reserved.</p>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
